import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import BasicInfoStep from './steps/BasicInfoStep';
import VoiceStep from './steps/VoiceStep';
import PersonalityStep from './steps/PersonalityStep';
import LanguageModelStep from './steps/LanguageModelStep';
import FinetuningStep from './steps/FinetuningStep';
import WizardProgress from './WizardProgress';
import { WizardData, WizardStep } from './types';
import AgentService from '../../../services/agentService';

interface AgentWizardProps {
  apiKey: string;
  onClose: () => void;
  onComplete: () => void;
}

const WIZARD_STEPS: WizardStep[] = [
  { id: 1, title: 'Basic Information', icon: '📝' },
  { id: 2, title: 'Voice Selection', icon: '🎤' },
  { id: 3, title: 'Agent Personality', icon: '✨' },
  { id: 4, title: 'Language & AI Model', icon: '🧠' },
  { id: 5, title: 'Fine-tuning', icon: '⚙️' },
];

const AgentWizard: React.FC<AgentWizardProps> = ({ apiKey, onClose, onComplete }) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [wizardData, setWizardData] = useState<WizardData>({
    // Basic Info
    name: '',
    purpose: '',
    
    // Voice Selection
    voiceId: '',
    ttsModel: 'eleven_turbo_v2',
    
    // Language & Personality
    primaryLanguage: 'en',
    additionalLanguages: [],
    systemPrompt: '',
    
    // AI Model
    llmProvider: 'google',
    llmModel: 'gemini-2.0-flash-001',
    temperature: 0.3,
    
    // Fine-tuning
    firstMessage: 'Hello! How can I help you today?',
    turnTimeout: 7,
    maxDuration: 600,
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Scroll to top when step changes
  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [currentStep]);

  const updateWizardData = (updates: Partial<WizardData>) => {
    setWizardData(prev => ({ ...prev, ...updates }));
  };

  const handleNext = () => {
    if (currentStep < WIZARD_STEPS.length) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleComplete = async () => {
    setLoading(true);
    setError(null);

    try {
      const agentService = new AgentService(apiKey);
      
      // Generate a name based on the purpose if not provided
      const purposeLabels: { [key: string]: string } = {
        customer_support: 'Customer Support Agent',
        outbound_sales: 'Sales Agent',
        learning_development: 'Training Agent',
        scheduling: 'Scheduling Assistant',
        lead_qualification: 'Lead Qualifier',
        answering_service: 'Answering Service',
        property_search: 'Property Search Assistant',
        viewing_appointments: 'Viewing Coordinator',
        market_information: 'Market Analyst',
        mortgage_guidance: 'Mortgage Advisor',
        listing_information: 'Listing Assistant',
        other: 'Real Estate Assistant'
      };
      
      const agentName = wizardData.name || purposeLabels[wizardData.purpose] || 'Real Estate AI Agent';
      
      const requestBody = {
        name: agentName,
        conversation_config: {
          agent: {
            first_message: wizardData.firstMessage,
            language: wizardData.primaryLanguage,
            prompt: {
              prompt: wizardData.systemPrompt,
              llm: wizardData.llmModel,
              temperature: wizardData.temperature,
              max_tokens: -1,
              tool_ids: [],
              built_in_tools: {}
            }
          },
          tts: {
            model_id: wizardData.ttsModel,
            voice_id: wizardData.voiceId,
            agent_output_audio_format: "pcm_16000",
            optimize_streaming_latency: 3,
            stability: 0.5,
            speed: 1,
            similarity_boost: 0.8,
          },
          asr: {
            quality: "high",
            provider: "elevenlabs",
            user_input_audio_format: "pcm_16000",
          },
          turn: {
            turn_timeout: wizardData.turnTimeout,
            silence_end_call_timeout: -1,
            mode: "turn",
          },
          conversation: {
            max_duration_seconds: wizardData.maxDuration,
            client_events: ["audio", "interruption"],
          }
        }
      };

      console.log('Creating agent with request body:', requestBody);
      await agentService.createAgent(requestBody);
      onComplete();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create agent');
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <BasicInfoStep
            data={wizardData}
            updateData={updateWizardData}
            onNext={handleNext}
          />
        );
      case 2:
        return (
          <VoiceStep
            data={wizardData}
            updateData={updateWizardData}
            onNext={handleNext}
            onPrevious={handlePrevious}
            apiKey={apiKey}
          />
        );
      case 3:
        return (
          <PersonalityStep
            data={wizardData}
            updateData={updateWizardData}
            onNext={handleNext}
            onPrevious={handlePrevious}
          />
        );
      case 4:
        return (
          <LanguageModelStep
            data={wizardData}
            updateData={updateWizardData}
            onNext={handleNext}
            onPrevious={handlePrevious}
          />
        );
      case 5:
        return (
          <FinetuningStep
            data={wizardData}
            updateData={updateWizardData}
            onComplete={handleComplete}
            onPrevious={handlePrevious}
            loading={loading}
            error={error}
          />
        );
      default:
        return null;
    }
  };

  return createPortal(
    <div ref={scrollContainerRef} className="fixed inset-0 bg-gray-900 z-50 overflow-y-auto">
      <div className="min-h-screen flex flex-col">
        <div className="max-w-6xl w-full mx-auto px-6 md:px-12 py-8 md:py-12 flex-1 flex flex-col">
          {/* Header */}
          <div className="flex justify-between items-start mb-8">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-2">
                <span className="gradient-text">Create Real Estate AI Agent</span>
              </h2>
              <p className="text-gray-400 text-sm md:text-base">
                Create an AI agent to handle your real estate business needs
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white text-2xl transition-colors hover:bg-white/10 rounded-lg p-2 flex-shrink-0"
            >
              ×
            </button>
          </div>

          {/* Progress */}
          <div className="mb-8">
            <WizardProgress steps={WIZARD_STEPS} currentStep={currentStep} />
          </div>

          {/* Step Content */}
          <div className="flex-1 flex flex-col justify-center">
            <div className="space-y-6">
              {renderStep()}
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AgentWizard;